import type { Chiave } from "../i18n/index.ts";
import { useT } from "./lib/i18n.tsx";
import { useCallback, useEffect, useState } from "react";
import { api } from "./lib/api.ts";
import { isUnauthorized } from "./lib/hooks.ts";
import { linkProps, useRoute, type Route } from "./lib/router.ts";
import type { Permission, Session } from "./lib/types.ts";
import { Spinner } from "./components/ui.tsx";
import { MenuUtente } from "./components/Tema.tsx";
import { AuditPage } from "./pages/Audit.tsx";
import { ImpostazioniPage } from "./pages/Impostazioni.tsx";
import { ProfiloPage } from "./pages/Profilo.tsx";
import { ImportPage } from "./pages/Import.tsx";
import { LoginPage } from "./pages/Login.tsx";
import { PortsPage } from "./pages/Ports.tsx";
import { SetupPage } from "./pages/Setup.tsx";
import { NewStackPage, StackDetailPage } from "./pages/StackDetail.tsx";
import { StacksPage } from "./pages/Stacks.tsx";

/**
 * Radice dell'applicazione.
 *
 * Decide cosa mostrare in base a tre condizioni: Portman è configurato?
 * c'è una sessione? e cosa può fare chi ha effettuato l'accesso?
 */

type Stato =
  | { fase: "caricamento" }
  | { fase: "primo-avvio" }
  | { fase: "accesso" }
  | { fase: "pronto"; sessione: Session };

export function App() {
  const [stato, setStato] = useState<Stato>({ fase: "caricamento" });

  /** Determina lo stato iniziale: configurazione e sessione in due passi. */
  const inizializza = useCallback(async () => {
    try {
      const sessione = await api.get<Session>("/auth/me");
      setStato({ fase: "pronto", sessione });
    } catch (error) {
      if (!isUnauthorized(error)) throw error;

      const { bootstrapped } = await api.get<{ bootstrapped: boolean }>("/setup/status");
      setStato({ fase: bootstrapped ? "accesso" : "primo-avvio" });
    }
  }, []);

  useEffect(() => {
    void inizializza();
  }, [inizializza]);

  if (stato.fase === "caricamento") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-6 tenue" />
      </div>
    );
  }

  if (stato.fase === "primo-avvio") return <SetupPage onCompletato={inizializza} />;
  if (stato.fase === "accesso") return <LoginPage onAccesso={inizializza} />;

  return <Guscio sessione={stato.sessione} onSessioneCambiata={inizializza} />;
}

/**
 * Voci di navigazione.
 *
 * L'evidenziazione guarda la rotta, non il percorso della finestra: le
 * schermate di uno stack (dettaglio, nuovo, importazione) sono figlie di
 * "Stack" e devono tenerla accesa, e la radice "/" è l'elenco degli stack.
 */
const VOCI: {
  path: string;
  etichetta: Chiave;
  permesso: Permission | Permission[];
  rotte: Route["name"][];
}[] = [
  {
    path: "/stacks",
    etichetta: "nav.stack",
    permesso: "stack:read",
    rotte: ["stacks", "stack", "new-stack", "import"],
  },
  { path: "/porte", etichetta: "nav.porte", permesso: "stack:read", rotte: ["ports"] },
  { path: "/attivita", etichetta: "nav.attivita", permesso: "audit:read", rotte: ["audit"] },
  {
    path: "/impostazioni",
    etichetta: "nav.impostazioni",
    // Chi gestisce solo gli utenti ci arriva comunque: la sezione Utenti vive lì.
    permesso: ["settings:manage", "user:manage"],
    rotte: ["settings"],
  },
];

/**
 * Cornice comune a tutte le schermate.
 *
 * Intestazione e contenuto condividono lo stesso contenitore a piena larghezza:
 * gli elenchi respirano, gli editor guadagnano ogni pixel e il bordo sinistro
 * resta lo stesso passando da una pagina all'altra.
 */
function Guscio({ sessione, onSessioneCambiata }: { sessione: Session; onSessioneCambiata: () => void }) {
  const route = useRoute();
  const t = useT();
  const può = (permesso: Permission) => sessione.permissions.includes(permesso);

  const esci = async () => {
    await api.post("/auth/logout");
    onSessioneCambiata();
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b bg-[var(--superficie-alta)]/85 backdrop-blur">
        {/* Su finestre strette la barra sbordava: le cinque voci più il blocco
            di destra non ci stanno, e il documento guadagnava uno scorrimento
            orizzontale su ogni schermata. Ora la navigazione scorre per conto
            suo e il resto si stringe, invece di spingere fuori la pagina. */}
        <div className="contenitore flex h-14 items-center gap-3 sm:gap-6">
          <a {...linkProps("/stacks")} className="flex shrink-0 items-center gap-2 font-semibold">
            <Logo />
            <span className="hidden sm:inline">Portman</span>{/* i18n-ignora */}
          </a>

          <nav className="senza-barra -mx-1 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-1 text-sm">
            {VOCI.filter((voce) => [voce.permesso].flat().some(può)).map((voce) => {
              const attiva = voce.rotte.includes(route.name);

              return (
                <a
                  key={voce.path}
                  {...linkProps(voce.path)}
                  aria-current={attiva ? "page" : undefined}
                  className={
                    attiva
                      ? "shrink-0 rounded-lg bg-accent-500/12 px-3 py-1.5 font-medium text-accent-700 dark:text-accent-500"
                      : "shrink-0 rounded-lg px-3 py-1.5 tenue transition-colors hover:bg-[var(--superficie-tenue)] hover:text-[var(--testo)]"
                  }
                >
                  {t.t(voce.etichetta)}
                </a>
              );
            })}
          </nav>

          <div className="shrink-0 text-sm">
            <MenuUtente sessione={sessione} onEsci={esci} />
          </div>
        </div>
      </header>

      {/* Il fondo si allarga quanto la console di un'operazione, che è ancorata
          là sotto: senza, le ultime righe di ogni schermata le finirebbero dietro. */}
      <main className="contenitore py-6 pb-[calc(1.5rem+var(--spazio-console,0px))]">
        <Schermata route={route} sessione={sessione} onSessioneCambiata={onSessioneCambiata} />
      </main>
    </div>
  );
}

function Schermata({
  route,
  sessione,
  onSessioneCambiata,
}: {
  route: ReturnType<typeof useRoute>;
  sessione: Session;
  /** Nome o password cambiati: si rilegge la sessione, che dopo un cambio password non c'è più. */
  onSessioneCambiata: () => void;
}) {
  const t = useT();
  switch (route.name) {
    case "stacks":
      return <StacksPage sessione={sessione} />;
    case "new-stack":
      return <NewStackPage />;
    case "import":
      return <ImportPage sessione={sessione} />;
    case "stack":
      // La chiave rilegge scheda e finestra dall'URL quando si passa a un altro stack.
      return <StackDetailPage key={route.stackId} stackId={route.stackId} sessione={sessione} />;
    case "ports":
      return <PortsPage />;
    case "audit":
      return <AuditPage />;
    case "settings":
      return <ImpostazioniPage sezione={route.sezione} sessione={sessione} />;
    case "profilo":
      return <ProfiloPage sessione={sessione} onCambiato={onSessioneCambiata} />;
    default:
      return (
        <div className="py-20 text-center">
          <p className="text-lg font-medium">{t.t("nav.nonTrovata")}</p>
          <a {...linkProps("/stacks")} className="mt-2 inline-block text-sm text-accent-600 hover:underline">
            {t.t("nav.tornaStack")}
          </a>
        </div>
      );
  }
}

function Logo() {
  return (
    <svg viewBox="0 0 100 100" className="size-6" aria-hidden>
      <rect width="100" height="100" rx="22" className="fill-accent-600" />
      <path
        d="M30 72V28h18a14 14 0 0 1 0 28H30"
        stroke="white"
        strokeWidth="9"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
